import React, { useEffect } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { Link } from 'react-router-dom';
import {
  FaArrowRight, FaCertificate, FaClock, FaCalendarAlt,
  FaTrophy, FaGlobe, FaCheck, FaExternalLinkAlt, FaAward, FaListUl
} from 'react-icons/fa';

/* ── tokens ── */
const T = {
  navy:       '#1a1a2e',
  indigo:     '#3730A3',
  indigoLight:'#f5f3ff',
  accent:     '#a78bfa',
  muted:      '#4a4a6a',
  border:     '#e8e6f0',
  bg:         '#ffffff',
  serif:      "'Times New Roman', serif",
};

/* ══════════════════════════════════════════
   GLOBAL
══════════════════════════════════════════ */
const GlobalStyle = createGlobalStyle`
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
`;

/* ══════════════════════════════════════════
   PAGE WRAPPER
══════════════════════════════════════════ */
const PageContainer = styled.div`
  margin-top: 195px;
  background: ${T.bg};
  font-family: ${T.serif};
  color: ${T.navy};

  @media (max-width: 1366px) and (min-width: 769px) { margin-top: 158px; }
  @media (max-width: 768px) { margin-top: 120px; }
`;

/* ══════════════════════════════════════════
   HERO
══════════════════════════════════════════ */
const Hero = styled.div`
  background: linear-gradient(160deg, #0b0b77 0%, #1a1050 30%, #3730A3 60%, #0b0b77 100%);
  display: flex;
  align-items: center;
  min-height: 300px;
  padding: 3.5rem 5%;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 5px;
    background: linear-gradient(180deg, ${T.accent} 0%, ${T.indigo} 100%);
  }

  &::after {
    content: '';
    position: absolute;
    right: -60px;
    top: -60px;
    width: 350px;
    height: 350px;
    background: radial-gradient(circle, rgba(167, 139, 250, 0.06) 0%, transparent 70%);
    border-radius: 50%;
    pointer-events: none;
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    min-height: 250px;
    padding: 2.5rem 5%;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    padding: 2.5rem 5%;
    min-height: auto;
    
    &::before { width: 100%; height: 4px; top: 0; left: 0; right: 0; bottom: auto; }
  }
`;

const HeroWatermark = styled.div`
  position: absolute;
  right: 3%;
  top: 50%;
  transform: translateY(-50%);
  font-family: ${T.serif};
  font-size: 180px;
  font-weight: 900;
  color: rgba(255,255,255,0.06);
  pointer-events: none;
  user-select: none;
  letter-spacing: -10px;
  line-height: 1;
  z-index: 1;

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 130px;
    right: 4%;
    color: rgba(255,255,255,0.06);
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  flex: 1;
  max-width: 800px;
  margin: 0 auto;
`;

const HeroTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(167, 139, 250, 0.1);
  border: 1px solid rgba(167, 139, 250, 0.25);
  color: ${T.accent};
  font-family: ${T.serif};
  font-size: 0.78rem;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  padding: 0.4rem 1rem;
  border-radius: 4px;
  margin-bottom: 1.5rem;
  font-weight: 700;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    background: ${T.accent};
    border-radius: 50%;
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 0.7rem;
    margin-bottom: 1rem;
  }
`;

const HeroTitle = styled.h1`
  font-family: ${T.serif};
  font-size: 4.1rem;
  font-weight: 900;
  color: #fff;
  line-height: 1.1;
  margin-bottom: 0.5rem;
  letter-spacing: -0.02em;

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 2.5rem;
  }

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-family: ${T.serif};
  font-size: 1.1rem;
  color: rgba(255,255,255,0.6);
  font-style: italic;
  margin-bottom: 1.8rem;

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 0.95rem;
    margin-bottom: 1.2rem;
  }
`;

const HeroStats = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
`;

const StatPill = styled.div`
  font-family: ${T.serif};
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.6rem 1.2rem;
  border: 1px solid rgba(255,255,255,0.15);
  border-radius: 6px;
  background: rgba(255,255,255,0.04);
  min-width: 100px;

  .val { 
    font-size: 1.3rem; 
    font-weight: 900; 
    color: ${T.accent}; 
    line-height: 1;
  }
  .lbl { 
    font-size: 0.7rem; 
    color: rgba(255,255,255,0.5); 
    text-transform: uppercase; 
    letter-spacing: 1px; 
    margin-top: 4px; 
    font-weight: 600;
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    padding: 0.5rem 1rem;
    min-width: 85px;
    .val { font-size: 1.1rem; }
    .lbl { font-size: 0.62rem; }
  }
`;

/* ══════════════════════════════════════════
   WRAP
══════════════════════════════════════════ */
const Wrap = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 2rem;

  @media (max-width: 768px) {
    padding: 0 1.2rem;
  }
`;

/* ══════════════════════════════════════════
   SECTION RULE
══════════════════════════════════════════ */
const SecRule = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  margin: 3rem 0 1.5rem;

  @media (max-width: 1366px) and (min-width: 769px) {
    margin: 2.5rem 0 1.2rem;
  }
`;

const RuleDot = styled.div`
  width: 8px; height: 8px;
  border-radius: 50%;
  background: ${T.indigo};
  flex-shrink: 0;
`;

const RuleLabel = styled.span`
  font-family: ${T.serif};
  font-size: 0.78rem;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: ${T.indigo};
  font-weight: 700;
  white-space: nowrap;

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 0.7rem;
  }
`;

const RuleLine = styled.div`
  flex: 1;
  height: 1px;
  background: ${T.border};
`;

/* ══════════════════════════════════════════
   BATCH STRIP
══════════════════════════════════════════ */
const BatchStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1px;
  border: 1px solid ${T.border};
  border-radius: 8px;
  overflow: hidden;
  background: ${T.border};

  @media (max-width: 650px) { grid-template-columns: 1fr; }
`;

const BatchCell = styled.div`
  background: #fff;
  padding: 1.2rem 1.5rem;
  font-family: ${T.serif};

  .b-name { 
    font-size: 0.75rem; 
    text-transform: uppercase; 
    letter-spacing: 1.5px; 
    color: ${T.indigo}; 
    font-weight: 700; 
    margin-bottom: 0.5rem; 
  }
  .b-days { 
    font-size: 1.1rem; 
    font-weight: 800; 
    color: ${T.navy}; 
  }
  .b-time { 
    font-size: 0.9rem; 
    color: ${T.muted}; 
    margin-top: 0.2rem; 
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    padding: 1rem 1.2rem;
    .b-days { font-size: 1rem; }
    .b-time { font-size: 0.82rem; }
  }
`;

/* ══════════════════════════════════════════
   TWO-PANEL
══════════════════════════════════════════ */
const TwoPanel = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;

  @media (max-width: 650px) { grid-template-columns: 1fr; }
`;

const TopicPanel = styled.div`
  background: #fff;
  border: 1px solid ${T.border};
  border-radius: 8px;
  overflow: hidden;
`;

const PanelHead = styled.div`
  background: ${T.navy};
  padding: 0.85rem 1.5rem;
  font-family: ${T.serif};
  font-size: 0.78rem;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: rgba(255,255,255,0.8);
  font-weight: 700;
`;

const TopicItem = styled.div`
  font-family: ${T.serif};
  display: flex;
  align-items: flex-start;
  gap: 0.8rem;
  padding: 0.7rem 1.5rem;
  border-bottom: 1px solid ${T.border};
  font-size: 0.95rem;
  color: ${T.muted};

  &:last-child { border-bottom: none; }

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 0.88rem;
    padding: 0.6rem 1.2rem;
  }
`;

const TopicNum = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  color: ${T.indigo};
  min-width: 28px;
  padding-top: 2px;
  font-style: italic;
`;

const BenefitPanel = styled.div`
  background: ${T.indigo};
  border-radius: 8px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const BenefitLabel = styled.div`
  font-family: ${T.serif};
  font-size: 0.75rem;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: rgba(255,255,255,0.6);
  font-weight: 700;
  margin-bottom: 1rem;
`;

const BenefitItem = styled.div`
  font-family: ${T.serif};
  display: flex;
  align-items: flex-start;
  gap: 0.7rem;
  padding: 0.6rem 0;
  border-bottom: 1px solid rgba(255,255,255,0.1);
  font-size: 0.95rem;
  color: rgba(255,255,255,0.9);
  line-height: 1.5;

  &:last-child { border-bottom: none; }

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 0.85rem;
  }
`;

const BenefitCheck = styled.div`
  width: 20px; height: 20px;
  background: rgba(167, 139, 250, 0.2);
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
  color: ${T.accent};
  font-size: 11px;
`;

/* ══════════════════════════════════════════
   WHO ATTENDS
══════════════════════════════════════════ */
const AttendeeGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.8rem;

  @media (max-width: 650px) { grid-template-columns: 1fr; }
`;

const AttendeeItem = styled.div`
  font-family: ${T.serif};
  display: flex;
  align-items: flex-start;
  gap: 0.8rem;
  background: #fff;
  border: 1px solid ${T.border};
  border-left: 3px solid ${T.indigo};
  border-radius: 0 6px 6px 0;
  padding: 0.8rem 1rem;
  font-size: 0.92rem;
  color: ${T.muted};
  line-height: 1.5;

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 0.85rem;
    padding: 0.7rem 0.9rem;
  }
`;

const AttendeeIdx = styled.span`
  font-family: ${T.serif};
  font-size: 1.2rem;
  font-style: italic;
  font-weight: 700;
  color: ${T.indigo};
  opacity: 0.25;
  min-width: 22px;
  line-height: 1;
  padding-top: 2px;
`;

/* ══════════════════════════════════════════
   CURRICULUM GRID
══════════════════════════════════════════ */
const CurrGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1px;
  background: ${T.border};
  border: 1px solid ${T.border};
  border-radius: 8px;
  overflow: hidden;

  @media (max-width: 650px) { grid-template-columns: 1fr; }
`;

const CurrModule = styled.div`
  background: ${({ $odd }) => $odd ? T.indigoLight : '#fff'};
  padding: 1.2rem 1.3rem;
  font-family: ${T.serif};
  position: relative;

  .mod-number {
    font-size: 2rem;
    font-weight: 700;
    color: ${T.indigo};
    opacity: 0.1;
    line-height: 1;
    position: absolute;
    top: 0.8rem;
    right: 0.8rem;
    font-style: italic;
  }

  .mod-title {
    font-size: 0.95rem;
    font-weight: 800;
    color: ${T.navy};
    margin-bottom: 0.6rem;
    line-height: 1.3;
    position: relative;
    z-index: 1;
  }

  ul {
    list-style: none;
    padding: 0;

    li {
      font-size: 0.85rem;
      color: ${T.muted};
      padding: 0.2rem 0;
      display: flex;
      align-items: flex-start;
      gap: 0.5rem;
      line-height: 1.5;

      &::before {
        content: '—';
        color: ${T.indigo};
        font-size: 0.75rem;
        flex-shrink: 0;
        margin-top: 2px;
      }
    }
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    padding: 1rem 1.1rem;
    .mod-title { font-size: 0.85rem; }
    ul li { font-size: 0.78rem; }
  }
`;

/* ══════════════════════════════════════════
   CERT BLOCK
══════════════════════════════════════════ */
const CertBlock = styled.div`
  background: #fff;
  border: 1px solid ${T.border};
  border-radius: 8px;
  overflow: hidden;
`;

const CertHead = styled.div`
  background: ${T.indigo};
  padding: 1.2rem 1.8rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.8rem;
`;

const CertHeadTitle = styled.div`
  font-family: ${T.serif};
  font-size: 1.1rem;
  color: #fff;
  font-weight: 800;
`;

const CertHeadBadge = styled.div`
  font-family: ${T.serif};
  background: rgba(167, 139, 250, 0.2);
  color: ${T.accent};
  border: 1px solid rgba(167, 139, 250, 0.3);
  font-size: 0.72rem;
  letter-spacing: 1px;
  text-transform: uppercase;
  padding: 0.3rem 0.8rem;
  border-radius: 4px;
  font-weight: 700;
`;

const CertBody = styled.div`
  padding: 1.8rem;
`;

const CertDesc = styled.p`
  font-family: ${T.serif};
  font-size: 0.95rem;
  color: ${T.muted};
  line-height: 1.8;
  margin-bottom: 1.8rem;
  border-left: 3px solid ${T.accent};
  padding-left: 1.2rem;

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 0.88rem;
  }
`;

const CertCols = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-bottom: 1.8rem;

  @media (max-width: 650px) { grid-template-columns: 1fr; }
`;

const CertColTitle = styled.div`
  font-family: ${T.serif};
  font-size: 0.78rem;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: ${T.indigo};
  font-weight: 700;
  margin-bottom: 0.8rem;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PlainList = styled.ul`
  list-style: none;
  padding: 0;

  li {
    font-family: ${T.serif};
    font-size: 0.92rem;
    color: ${T.muted};
    padding: 0.35rem 0;
    border-bottom: 1px solid ${T.border};
    display: flex;
    align-items: flex-start;
    gap: 0.6rem;
    line-height: 1.5;

    &:last-child { border-bottom: none; }

    svg { color: ${T.indigo}; font-size: 0.8rem; margin-top: 3px; flex-shrink: 0; }
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    li { font-size: 0.85rem; }
  }
`;

const FeeTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: ${T.serif};
  margin-bottom: 1rem;

  th {
    background: ${T.navy};
    color: rgba(255,255,255,0.7);
    font-size: 0.75rem;
    letter-spacing: 1px;
    text-transform: uppercase;
    padding: 0.7rem 1.2rem;
    text-align: left;
    font-weight: 700;
  }

  td {
    padding: 0.9rem 1.2rem;
    border-bottom: 1px solid ${T.border};
    font-size: 0.95rem;
    color: ${T.muted};
  }

  tr:last-child td { border-bottom: none; }

  .amt {
    font-size: 1.5rem;
    font-weight: 900;
    color: ${T.navy};
  }

  tr.highlight { background: ${T.indigoLight}; }
  tr.highlight .amt { color: ${T.indigo}; }
  tr.highlight td { border-bottom: none; }
`;

const FeeNoteTag = styled.span`
  display: inline-block;
  font-size: 0.7rem;
  background: rgba(55,48,163,0.1);
  color: ${T.indigo};
  padding: 0.2rem 0.6rem;
  border-radius: 4px;
  margin-left: 0.5rem;
  vertical-align: middle;
  font-weight: 700;
`;

const CertLinkRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding-top: 0.8rem;
  border-top: 1px solid ${T.border};

  a {
    font-family: ${T.serif};
    color: ${T.indigo};
    font-size: 0.92rem;
    font-weight: 700;
    text-decoration: none;
    &:hover { text-decoration: underline; }
  }

  svg { color: ${T.indigo}; font-size: 0.85rem; }
`;

/* ══════════════════════════════════════════
   CTA
══════════════════════════════════════════ */
const CTASection = styled.div`
  margin: 3rem 0 1rem;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  background: ${T.navy};
  border-radius: 8px;
  overflow: hidden;
  padding: 1.5rem 1.5rem 1.5rem 0;

  &::before {
    content: '';
    width: 5px;
    align-self: stretch;
    background: ${T.accent};
    flex-shrink: 0;
  }

  @media (max-width: 650px) {
    flex-direction: column;
    padding: 1.5rem;
    &::before { display: none; }
  }
`;

const CTAText = styled.div`
  flex: 1;
  .cta-title {
    font-family: ${T.serif};
    font-size: 1.4rem;
    font-weight: 800;
    color: #fff;
    margin-bottom: 0.3rem;
  }
  .cta-sub {
    font-family: ${T.serif};
    font-size: 0.95rem;
    color: rgba(255,255,255,0.55);
    font-style: italic;
  }
`;

const CTAButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: ${T.accent};
  color: ${T.navy};
  font-family: ${T.serif};
  font-weight: 700;
  font-size: 0.95rem;
  padding: 0.8rem 1.8rem;
  border-radius: 50px;
  text-decoration: none;
  white-space: nowrap;
  transition: all 0.3s ease;
  flex-shrink: 0;

  &:hover { 
    background: #c4b5fd; 
    transform: translateY(-2px);
  }
`;

const Spacer = styled.div`
  height: 2rem;
`;

/* ══════════════════════════════════════════
   DATA
══════════════════════════════════════════ */
const batches = [
  { name: 'Batch 1', days: 'Sat & Sun',  time: '10:30 AM – 01:30 PM' },
  { name: 'Batch 2', days: 'Sat & Sun',  time: '02:30 PM – 05:30 PM' },
  { name: 'Batch 3', days: 'Mon – Thu',  time: '06:00 PM – 08:00 PM' },
];

const topics = [
  'Overview of U.S. Healthcare',
  'Overview of Reimbursement Methodologies',
  'Medical Record Documentation',
  'U.S. Healthcare Legal & Regulatory Guidelines',
  'Compliance, Edits, & Reports',
  'ICD-10 CM Coding',
  'ICD-10 PCS Coding',
  'Current Procedural Terminology (CPT)',
];

const romanNumerals = ['I.', 'II.', 'III.', 'IV.', 'V.', 'VI.', 'VII.', 'VIII.'];

const benefits = [
  'Long-term stable career with global opportunities (U.S., Middle East, etc.)',
  'Comprehensive hospital & physician practice coding knowledge',
  'Increases earning potential & employability',
  'Boosts confidence & overall performance',
  '98% passing rate with Trainee Satisfaction',
  'Placement assistance for merit candidates',
];

const whoShouldAttend = [
  'Medical, Paramedical & Life Science Graduates (M.B.B.S., B.D.S., B.H.M.S., B.A.M.S., Pharmacy, Physiotherapy, Nursing, Microbiology, Biochemistry, Biotechnology, Genetics, Biology, Zoology, Botany, etc.)',
  'Candidates in outpatient settings (ED/Facility, E&M Coding) seeking career advancement',
  'HCC Coders exploring new career options',
  'Candidates wanting to learn Inpatient DRG Coding',
  'Professionals seeking Middle East opportunities',
  'Middle East professionals advancing with Inpatient Coding',
  'Candidates planning U.S. employment',
];

const curriculum = [
  { title: 'U.S. Healthcare Overview',       items: ['Revenue Cycle Management', 'Insurance Providers', 'Billing Forms'] },
  { title: 'Reimbursement Methodologies',    items: ['IPPS', 'DRGs & Relative Weights', 'Payment Adjustments', 'Outlier Payments', 'Transfer Policies'] },
  { title: 'Medical Record Documentation',   items: ['JCAHO Guidelines', 'UHDDS Definitions & Application'] },
  { title: 'Legal & Regulatory Guidelines',  items: ['Affordable Care Act', 'HIPAA', 'HITECH Act'] },
  { title: 'Compliance, Edits & Reports',    items: ['Compliance Policies', 'NCCI, MCE, MUE', 'FATHOM, PEPPER, CERT, CBR', 'Coding Clinic Updates'] },
  { title: 'ICD-10 CM Coding',               items: ['Structure, Format & Conventions', 'General & Chapter Guidelines'] },
  { title: 'CPT Coding',                     items: ['CPT Structure & Sections', 'E&M Coding', 'Surgery Guidelines & Modifiers', 'Radiology, Lab & Medicine Sections'] },
  { title: 'ICD-10-PCS Coding',              items: ['PCS Structure & Format', 'Coding Guidelines', 'Sections & Root Operations'] },
  { title: 'Practice Modules',               items: ['Outpatient Case Scenarios', 'Inpatient Case Scenarios'] },
];

const eligibility = [
  'Credential: RHIA®, RHIT®, or CCS-P®',
  'Education: Anatomy, physiology, pathophysiology, pharmacology, medical terminology, reimbursement, ICD/CPT coding',
  'Experience: 2+ years coding experience',
  'CCA® + 1 year coding experience',
  'Other coding credential + 1 year experience',
];

const inclusions = [
  '80–100 Hours Expert Training',
  'Study Materials & Practice Exercises',
  'Mock Exams (AHIMA Pattern)',
  'One-on-One Mentoring',
  'Placement Assistance',
  'Certificate of Completion',
];

/* ══════════════════════════════════════════
   COMPONENT
══════════════════════════════════════════ */
const CCS = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <>
      <GlobalStyle />
      <PageContainer>

        {/* ── HERO ── */}
        <Hero>
          <HeroWatermark>CCS</HeroWatermark>
          <HeroContent>
            
            <HeroTitle>Certified Coding Specialist (CCS)</HeroTitle>
            <HeroSubtitle>Training provided by AHIMA certified trainer</HeroSubtitle>
           
          </HeroContent>
        </Hero>

        <Wrap>
          {/* ── BATCHES ── */}
          <SecRule>
            <RuleDot /><RuleLabel>Available Batches</RuleLabel><RuleLine />
          </SecRule>
          <BatchStrip>
            {batches.map((b, i) => (
              <BatchCell key={i}>
                <div className="b-name">{b.name}</div>
                <div className="b-days">{b.days}</div>
                <div className="b-time">{b.time}</div>
              </BatchCell>
            ))}
          </BatchStrip>

          {/* ── TOPICS + BENEFITS ── */}
          <SecRule>
            <RuleDot /><RuleLabel>Topics &amp; Benefits</RuleLabel><RuleLine />
          </SecRule>
          <TwoPanel>
            <TopicPanel>
              <PanelHead>Topics Covered</PanelHead>
              {topics.map((t, i) => (
                <TopicItem key={i}>
                  <TopicNum>{romanNumerals[i]}</TopicNum>
                  <span>{t}</span>
                </TopicItem>
              ))}
            </TopicPanel>

            <BenefitPanel>
              <BenefitLabel>Program Benefits</BenefitLabel>
              {benefits.map((b, i) => (
                <BenefitItem key={i}>
                  <BenefitCheck><FaCheck /></BenefitCheck>
                  {b}
                </BenefitItem>
              ))}
            </BenefitPanel>
          </TwoPanel>

          {/* ── WHO SHOULD ATTEND ── */}
          <SecRule>
            <RuleDot /><RuleLabel>Who Should Attend</RuleLabel><RuleLine />
          </SecRule>
          <AttendeeGrid>
            {whoShouldAttend.map((item, i) => (
              <AttendeeItem key={i}>
                <AttendeeIdx>{i + 1}</AttendeeIdx>
                <span>{item}</span>
              </AttendeeItem>
            ))}
          </AttendeeGrid>

          {/* ── CURRICULUM ── */}
          <SecRule>
            <RuleDot /><RuleLabel>Course Curriculum</RuleLabel><RuleLine />
          </SecRule>
          <CurrGrid>
            {curriculum.map((mod, i) => (
              <CurrModule key={i} $odd={i % 2 !== 0}>
                <div className="mod-number">{i + 1}</div>
                <div className="mod-title">{mod.title}</div>
                <ul>
                  {mod.items.map((item, j) => <li key={j}>{item}</li>)}
                </ul>
              </CurrModule>
            ))}
          </CurrGrid>

          {/* ── CERTIFICATION & FEES ── */}
          <SecRule>
            <RuleDot /><RuleLabel>Certification &amp; Pricing</RuleLabel><RuleLine />
          </SecRule>
          <CertBlock>
            <CertHead>
              <CertHeadTitle>CCS Certification — AHIMA</CertHeadTitle>
              <CertHeadBadge>Globally Recognized</CertHeadBadge>
            </CertHead>
            <CertBody>
              <CertDesc>
                AHIMA offers CCS as a premium globally recognized certification (US, Middle East, India).
                It demonstrates proficiency in hospital inpatient &amp; outpatient coding and validates
                expert-level knowledge in HIM and data quality.
              </CertDesc>

              <CertCols>
                <div>
                  <CertColTitle><FaAward /> Eligibility (AHIMA)</CertColTitle>
                  <PlainList>
                    {eligibility.map((e, i) => (
                      <li key={i}><FaCheck />{e}</li>
                    ))}
                  </PlainList>
                </div>
                <div>
                  <CertColTitle><FaListUl /> Course Inclusions</CertColTitle>
                  <PlainList>
                    {inclusions.map((inc, i) => (
                      <li key={i}><FaCheck />{inc}</li>
                    ))}
                  </PlainList>
                </div>
              </CertCols>

              <FeeTable>
                <thead>
                  <tr>
                    <th>Component</th>
                    <th>Description</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>AHIMA Membership</strong></td>
                    <td>To be paid to AHIMA directly</td>
                    <td><span className="amt">$79</span></td>
                  </tr>
                  <tr>
                    <td><strong>CCS Exam Fee</strong></td>
                    <td>Certification examination fee to be paid to AHIMA directly</td>
                    <td><span className="amt">$299</span></td>
                  </tr>
                  <tr className="highlight">
                    <td><strong>Our Course Fee</strong></td>
                    <td>To be paid to LearnThruUs Academy </td>
                    <td><span className="amt">₹17,000 + GST</span></td>
                  </tr>
                </tbody>
              </FeeTable>

              <CertLinkRow>
                <FaExternalLinkAlt />
                <a
                  href="http://www.ahima.org/certification/CCS?tabid=international"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Visit AHIMA for full certification details
                </a>
              </CertLinkRow>
            </CertBody>
          </CertBlock>

          {/* ── CTA ── */}
          <CTASection>
            <CTAText>
              <div className="cta-title">Ready to Get CCS Certified?</div>
              <div className="cta-sub">Join thousands of successful graduates and start your career today.</div>
            </CTAText>
            <CTAButton to="/contact-us">
              Enroll Now <FaArrowRight />
            </CTAButton>
          </CTASection>

          <Spacer />
        </Wrap>
      </PageContainer>
    </>
  );
};

export default CCS;